/**
 * @prettier
 * @flow
 */
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { navigate } from '@reach/router';
import React from 'react';
import { useForm } from 'react-hook-form';
import { paymentLinkApi } from 'src/api/paymentLinkApi';
import { Form } from 'src/components/form/Form';
import { FormSubmitButton } from 'src/components/form/FormSubmitButton';
import { FormPasswordField } from 'src/components/form/sign-in/FormPasswordField';
import { FormUsernameField } from 'src/components/form/sign-in/FormUsernameField';
import { setAccessTokenInLocalStorage } from 'src/localStorage/setAccessTokenInLocalStorage';
import { setAccountIdInLocalStorage } from 'src/localStorage/setAccountIdInLocalStorage';
import { setEmailInLocalStorage } from 'src/localStorage/setEmailInLocalStorage';
import { setRefreshTokenInLocalStorage } from 'src/localStorage/setRefreshTokenInLocalStorage';
import { actions } from 'src/reducers';
import { encryptDataInTransit } from 'src/utils/crypto/encryptDataInTransit';
import { useAction } from 'src/utils/react/useAction';
import { useSelector } from 'src/utils/react/useSelector';
import { translate } from 'src/utils/translate/translate';

export function SignInPage() {
    const classes = useStyles();

    const form = useForm<FormType>();

    const signedOutReason = useSelector(state => state.app.signedOutReason);
    const signInFailureReason = useSelector(state => state.app.signInFailureReason);

    const signInUser = useAction(actions.signInUser);
    const signOutUser = useAction(actions.signOutUser);
    const showAppSnackBarMessage = useAction(actions.showAppSnackBarMessage);
    const setSignInFailureReason = useAction(actions.setSignInFailureReason);

    const onSubmit = async (form: FormType) => {
        const response = await paymentLinkApi.signInUser({
            email: form.email,
            password: encryptDataInTransit(form.password),
        });
        showAppSnackBarMessage({ message: translate('Signing in failed') });
        if (!response.ok) {
            if (response.problem === 'NETWORK_ERROR') {
                setSignInFailureReason(translate('Network error, please check your internet connection.'));
                return;
            }
            if (response.status === 401) {
                setSignInFailureReason(translate('Failed to sign in, please check your email and password.'));
                return;
            }
            setSignInFailureReason(translate('Failed to sign in, please try again if problem persists please contact support.'));
            return;
        }

        setEmailInLocalStorage(response.data.email);
        setAccountIdInLocalStorage(response.data.accountId);
        setAccessTokenInLocalStorage(response.data.accessToken);
        setRefreshTokenInLocalStorage(response.data.refreshToken);

        navigate(`../`);
        signInUser({
            email: response.data.email,
            accountId: response.data.accountId,
            accessToken: response.data.accessToken,
            refreshToken: response.data.refreshToken,
        });
    };

    return (
        <Paper className={classes.paper}>
            <Typography variant='h4' align='center' className={classes.header}>
                {translate('Sign In')}
            </Typography>
            <Form form={form} onSubmit={onSubmit}>
                <Grid container spacing={3} justifyContent='center' alignItems='center'>
                    <Grid item xs={12} sm={6}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormUsernameField name='email' label={translate('Email')} /> {/*rules={{ required: true, minLength: 4, maxLength: 100 }} />*/}
                            </Grid>
                            <Grid item xs={12}>
                                <FormPasswordField name='password' label={translate('Password')} /> {/* rules={{ required: true }} /> */}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid container spacing={2} direction='column' justifyContent='space-around' alignItems='center' className={classes.buttonContainer}>
                    <Grid item>
                        <FormSubmitButton text={translate('Sign In')} />
                    </Grid>
                    {!!(signInFailureReason || signedOutReason) && (
                        <Grid item>
                            <Grid container spacing={2} direction='column' justifyContent='center' alignItems='center'>
                                <Grid item>
                                    <Typography variant='body2' color='error'>
                                        {signInFailureReason ?? signedOutReason}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Form>
        </Paper>
    );
}

const useStyles = makeStyles(theme => ({
    header: {
        paddingBottom: theme.spacing(4),
    },
    backButtonContainer: {
        position: 'absolute',
    },
    paper: {
        padding: theme.spacing(2),
        [theme.breakpoints.up(800 + theme.spacing(3) * 2)]: {
            padding: theme.spacing(3),
        },
    },
    buttonContainer: {
        paddingTop: theme.spacing(4),
    },
}));

type FormType = {|
    email: string,
    password: string,
|};
